import { useState } from "react";
import { X, Check, Sparkle } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const PERKS = [
  "Unlimited peer matches (not just top 5)",
  "Journey Analytics dashboard with match distribution",
  "Priority AI Health Navigator responses",
  "Advanced search across all condition hubs",
  "Ad-free, forever, private by design",
];

export default function PremiumDialog({ open, onClose }) {
  const [busy, setBusy] = useState(false);
  if (!open) return null;

  const upgrade = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/premium/checkout/session", {
        package_id: "premium_monthly",
        origin_url: window.location.origin,
      });
      if (!data?.url) throw new Error("no url");
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-ink/50 backdrop-blur-sm grid place-items-center p-4" onClick={onClose} data-testid="premium-dialog">
      <div className="bg-cream rounded-2xl w-full max-w-lg shadow-hero overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="relative p-8 bg-ink text-cream">
          <button onClick={onClose} data-testid="premium-close" className="absolute top-4 right-4 p-1.5 rounded-full hover:bg-white/10"><X size={18} /></button>
          <div className="text-xs tracking-[0.25em] uppercase text-coral-700 mb-3"><span className="rule bg-coral opacity-100" />Wingwomen Premium</div>
          <h3 className="font-display text-3xl leading-tight">Go deeper.<br />Stay anonymous.</h3>
          <div className="mt-4 inline-flex items-center gap-1.5 text-[10px] tracking-[0.2em] uppercase text-coral bg-coral/10 border border-coral/30 rounded-full px-3 py-1">
            <Sparkle size={10} weight="fill" aria-hidden="true" /> 14 days free, then
          </div>
          <div className="mt-3 flex items-baseline gap-2">
            <span className="font-display text-5xl">$14.99</span>
            <span className="text-cream/70">/ month</span>
          </div>
          <p className="text-cream/70 text-sm mt-2">Cancel anytime during your trial, never pay if it's not right for you.</p>
        </div>
        <div className="p-6 space-y-3">
          {PERKS.map((p) => (
            <div key={p} className="flex items-start gap-3">
              <div className="mt-0.5 w-5 h-5 rounded-full bg-coral-50 border border-coral-100 grid place-items-center shrink-0">
                <Check size={12} weight="bold" className="text-coral-700" />
              </div>
              <div className="text-sm text-ink/80 leading-relaxed">{p}</div>
            </div>
          ))}
          <button onClick={upgrade} disabled={busy} data-testid="premium-upgrade"
            className="w-full mt-4 bg-coral text-white py-3 rounded-full font-medium hover:bg-coral-600 disabled:opacity-60 transition-colors flex items-center justify-center gap-2">
            <Sparkle size={16} weight="duotone" /> {busy ? "Redirecting to Stripe…" : "Start your 14-day free trial"}
          </button>
          <p className="text-[10px] text-center text-muted-foreground uppercase tracking-widest mt-3">
            Secured by Stripe · card required, charged only after your trial ends
          </p>
          <div className="flex items-center justify-center gap-3 mt-3 text-[10px] text-muted-foreground/80">
            <span className="flex items-center gap-1"><Check size={10} weight="bold" className="text-sage" /> Cancel any time</span>
            <span className="text-border">·</span>
            <span className="flex items-center gap-1"><Check size={10} weight="bold" className="text-sage" /> PCI-DSS by Stripe</span>
            <span className="text-border">·</span>
            <span className="flex items-center gap-1"><Check size={10} weight="bold" className="text-sage" /> HIPAA-aligned</span>
          </div>
        </div>
      </div>
    </div>
  );
}
